import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { hsCodeAPI, providerAPI } from '../../services/api';

const HsCodeManager = () => {
  const [hsCodes, setHsCodes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHsCode, setNewHsCode] = useState({
    manufacturerId: '',
    hsCode: '',
    description: '',
    category: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // First get providers
      const providersResponse = await providerAPI.getAll();
      const providersList = providersResponse.data || [];
      setProviders(providersList);
      
      // Then get HS codes from each provider
      const hsResults = await Promise.all(
        providersList.map(p => 
          hsCodeAPI.getForManufacturer(p.id).catch(() => ({ data: [] }))
        )
      );
      const allHsCodes = hsResults.flatMap(r => r.data || []);
      setHsCodes(allHsCodes);
    } catch (err) {
      setError('Failed to load HS codes: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchCode.trim()) return;

    try {
      setLoading(true);
      const response = await hsCodeAPI.lookup({ code: searchCode.trim() });
      setSearchResult(response.data);
    } catch (err) {
      setSearchResult({ error: err.message || 'HS code not found' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddHsCode = async (e) => {
    e.preventDefault();
    try {
      await hsCodeAPI.create(newHsCode.manufacturerId, {
          hsCode: newHsCode.hsCode,
          description: newHsCode.description,
          category: newHsCode.category
        });
      setShowAddForm(false);
      setNewHsCode({ manufacturerId: '', hsCode: '', description: '', category: '' });
      fetchData();
    } catch (err) {
      setError('Failed to add HS code: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this HS code?')) return;
    
    try {
      const hsCode = hsCodes.find(h => h.id === id);
      if (hsCode) {
        await hsCodeAPI.delete(hsCode.manufacturerId, id);
      }
      fetchData();
    } catch (err) {
      setError('Failed to delete HS code: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading && hsCodes.length === 0) {
    return <div className="loading">Loading HS codes...</div>;
  }

  return (
    <div className="hs-code-manager">
      <div className="page-header">
        <h1>HS Code Manager</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add HS Code'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Search Section */}
      <div className="form-section">
        <h2>Lookup HS Code</h2>
        <form onSubmit={handleSearch} className="filters">
          <input
            type="text"
            className="filter-input"
            placeholder="Enter HS code (e.g., 6907.21)"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        {searchResult && (
          <div className="detail-section" style={{ marginTop: '1rem' }}>
            {searchResult.error ? (
              <p className="text-muted">{searchResult.error}</p>
            ) : (
              <>
                <h3>Search Result</h3>
                <p><strong>HS Code:</strong> {searchResult.hsCode}</p>
                <p><strong>Description:</strong> {searchResult.description}</p>
                <p><strong>Category:</strong> {searchResult.category}</p>
                {searchResult.fallbackUsed && (
                  <p className="badge badge-warning">Fallback match used</p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="form-section">
          <h2>Add New HS Code</h2>
          <form onSubmit={handleAddHsCode}>
            <div className="form-row">
              <div className="form-group">
                <label>Provider</label>
                <select
                  className="filter-select"
                  value={newHsCode.manufacturerId}
                  onChange={(e) => setNewHsCode({...newHsCode, manufacturerId: e.target.value})}
                  required
                >
                  <option value="">Select Provider</option>
                  {providers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>HS Code</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="e.g., 6907.21"
                  value={newHsCode.hsCode}
                  onChange={(e) => setNewHsCode({...newHsCode, hsCode: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Product description"
                  value={newHsCode.description}
                  onChange={(e) => setNewHsCode({...newHsCode, description: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="e.g., Ceramic"
                  value={newHsCode.category}
                  onChange={(e) => setNewHsCode({...newHsCode, category: e.target.value})}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Save HS Code</button>
            </div>
          </form>
        </div>
      )}

      {/* HS Codes List */}
      <div className="form-section">
        <h2>Registered HS Codes ({hsCodes.length})</h2>
        {hsCodes.length === 0 ? (
          <div className="empty-state">
            <p>No HS codes registered yet</p>
          </div>
        ) : (
          <table className="simple-table">
            <thead>
              <tr>
                <th>HS Code</th>
                <th>Description</th>
                <th>Category</th>
                <th>Provider</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hsCodes.map((hs) => (
                <tr key={hs.id}>
                  <td><code>{hs.hsCode}</code></td>
                  <td>{hs.description}</td>
                  <td>
                    <span className="badge badge-tag">{hs.category}</span>
                  </td>
                  <td>
                    {providers.find(p => p.id === hs.manufacturerId)?.name || 'Unknown'}
                  </td>
                  <td>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(hs.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default HsCodeManager;
